"use client";

import { useState, useEffect, createContext, useContext } from "react";

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Use a null initial state to prevent hydration mismatch
  const [theme, setTheme] = useState<Theme | null>(null);
  const [mounted, setMounted] = useState(false);
  
  // Only determine isDark after mounting
  const isDark = theme === 'dark';

  useEffect(() => {
    // Check if user has a theme preference stored, otherwise default to light mode
    const savedTheme = localStorage.getItem('theme') as Theme;
    
    // Determine initial theme, defaulting to light mode
    const initialTheme = savedTheme || 'light';
    setTheme(initialTheme);
    
    // Apply theme to document
    if (initialTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    setMounted(true);
  }, []);

  // Function to toggle theme
  const toggleTheme = () => {
    if (!theme) return; // Safety check
    
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Use a consistent value for server and initial client render
  const defaultTheme: ThemeContextType = {
    theme: 'light',
    isDark: false,
    toggleTheme: () => {},
  };

  // Return early with a non-interactive UI if not mounted
  if (!mounted) {
    return (
      <ThemeContext.Provider value={defaultTheme}>
        {children}
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider 
      value={{ 
        theme: theme || 'light', 
        isDark: isDark,
        toggleTheme 
      }}
    >
      <div suppressHydrationWarning>{children}</div>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}